import React, { useState, useEffect } from 'react';
import { Navbar } from '../shared/Navbar';
import { MessageSquare, ChevronDown, Glasses, FileText, SquareGanttChart as SquareGantt, Layers, MonitorPlay, Target, PenLine, Scale } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * The LAW-SCHOOL context navbar — `/law-school` and `/law-school/unlimited`.
 *
 * ── IT USED TO BE THE HOMEPAGE NAVBAR. THAT IS THE WHOLE CHANGE. ────────────
 * Until the 2026-09-11 cutover this component served `/` (HomePage.tsx) and
 * every anchor it rendered pointed at `/#<section>`. `/` is now
 * HomeVariantBPage, which is a ROUTER rather than a product page and has its
 * own navbar (src/components/marketing/MarketingRootNavbar.tsx). The product
 * content those anchors described moved to `/law-school/unlimited`, so this
 * navbar moved with it.
 *
 * THERE WERE FOUR `pathname === '/'` ASSUMPTIONS AND MISSING ANY ONE OF THEM
 * LEAVES A NAVBAR THAT LOOKS FINE AND IS BROKEN:
 *   1. `featureItems` hrefs — the links themselves.
 *   2. `handleHashLink` — when off-page it did `navigate('/#' + hash)`, so
 *      repointing the hrefs alone would have kept sending clicks to the OLD
 *      page regardless of what the href said.
 *   3. `isActive` — returned false whenever pathname !== '/', so the active
 *      pill would never light on the new page.
 *   4. the SCROLL-SPY guard — `if (location.pathname !== '/') return`.
 * All four now key off `PRODUCT_PATH` below. This is the list from
 * site-navigation.json's `_pendingAnchorPages` todo, which had three of them;
 * the spy guard is the fourth and is the one that would have gone wrong
 * silently, because it fails by simply never running.
 *
 * ── TRAILING SLASHES ARE NORMALISED ON BOTH SIDES ──────────────────────────
 * `/law-school/unlimited/` must light up exactly like `/law-school/unlimited`.
 * An exact compare makes it miss, and because every isActive() call and the spy
 * guard short-circuit on the same predicate, NOTHING highlights — no error, no
 * console warning, just a navbar that looks permanently unvisited. A trailing
 * slash arrives from hand-typed URLs, pasted links and canonicalising redirects
 * alike; it is not a typo you can design out. Same defect, same fix, in
 * shared/Navbar.tsx's `samePath` and in the two marketing navbars.
 *
 * ── THE SHAPE (designer "Nav Design" 3b) ───────────────────────────────────
 * Logo → divider → "LAW SCHOOL" in jdblue, then Features ▾ / Outlines /
 * Pricing, then the cross-product link to NextGen Practice, then Blog. No
 * "Home" item: the logo is the home affordance and it goes to `/`, which is now
 * a different page from this one.
 *
 * Community is NOT here and must not be added when `community-board` goes
 * production-ON. Founder 2026-09-11: the in-app sidebar is the surface that
 * reaches the real community; `/community` is not a route at all.
 */

/**
 * The page this navbar's anchors resolve against.
 *
 * ONE CONSTANT, FOUR CONSUMERS (hrefs, hash navigation, isActive, the spy
 * guard). The hrefs below still spell it out literally — site-navigation.json
 * declares them and scripts/check-nav-drift.mjs scans them LITERALLY, so a
 * templated href reads as MISSING to that gate. The constant governs the three
 * PREDICATES, which is where the silent failures live.
 */
const PRODUCT_PATH = '/law-school/unlimited';

const stripSlash = (p: string) => p.replace(/\/+$/, '') || '/';

export function AskJDSNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('top');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isOnProductPage = stripSlash(location.pathname) === stripSlash(PRODUCT_PATH);

  // Handle smooth scrolling to section IDs when hash links are clicked
  useEffect(() => {
    // Short delay to ensure the DOM has loaded
    const timeoutId = setTimeout(() => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (id === 'top') {
          // Special case for #top if element not found
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.hash]);

  // Scroll-based section detection
  useEffect(() => {
    if (!isOnProductPage) return;

    // The SET of spied sections. Two rules, both verified against the AST
    // rather than by eye — a substring grep counts ids inside COMMENTS and will
    // lie to you here:
    //
    //   1. Every entry must be an id LawSchoolUnlimitedPage actually renders.
    //      When this list belonged to HomePage it had SIX ids that existed
    //      nowhere in the repo, plus `courses`, which existed only in a
    //      component the page did not render.
    //   2. Every entry must have a nav item pointing at it, and every nav
    //      anchor must appear here. An id spied with NO nav item is the worse
    //      direction: activeSection holds a value no isActive check matches and
    //      the whole navbar renders unhighlighted over that range. ('top' is
    //      the one exemption — it is the default activeSection and the logo's
    //      target, not a nav item.)
    //
    // Sibling of the same defect in the since-retired NextGenPrepNavbar.tsx,
    // whose repaired form MarketingBarNavbar inherited. These are
    // logical twins with no vendoring relationship, so
    // scripts/check-shared-drift.sh cannot see them — if you change the shape
    // here, change it there too (.claude/rules/propagate-the-fix.md).
    //
    // EVERY id listed must be UNCONDITIONALLY RENDERED AND VISIBLE. A
    // `display:none` element measures an all-zero rect, so its absolute top
    // resolves to the CURRENT SCROLL POSITION and it migrates through the
    // sorted order as the visitor scrolls, corrupting a different pair of
    // boundaries at every offset. Do not add a `hidden md:block` variant.
    const sections = [
      // 'top' is the DEFAULT activeSection and the only entry with no nav item
      // pointing at it — the one exemption rule 2 allows. `problem` was in this
      // list in an earlier draft and is deliberately NOT: /law-school/unlimited
      // renders id="problem", but no item in this navbar links to it, and a
      // spied id with no nav item is the worse direction (activeSection holds a
      // value no isActive check matches, and the ENTIRE navbar renders
      // unhighlighted over that range).
      'top',
      'practice',
      'library',
      'caselaw',
      'notes',
      'chat',
      'pricing',
    ];

    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + window.innerHeight * 0.3; // Offset for better UX

      // Measure once, then order by MEASURED position — never by the order of
      // `sections` above.
      //
      // The boundary walk below derives each section's bottom from the NEXT
      // entry's top, so a list disagreeing with the DOM silently produces
      // inverted ranges (top > bottom) that can never match. Sorting on real
      // geometry removes the array-order invariant entirely instead of
      // restating it.
      const measured = sections
        .map(id => {
          const element = document.getElementById(id);
          return element ? { id, top: element.getBoundingClientRect().top + window.scrollY } : null;
        })
        .filter((item): item is { id: string; top: number } => item !== null)
        .sort((a, b) => a.top - b.top);

      // The last section owns the rest of the document; every other section
      // ends where the next one begins.
      const sectionBoundaries = measured.map(({ id, top }, idx) => ({
        id,
        top,
        bottom: idx === measured.length - 1 ? document.body.scrollHeight : measured[idx + 1].top,
      }));

      // Find which section contains the current scroll position
      let activeId = 'top'; // default

      for (const section of sectionBoundaries) {
        if (scrollPosition >= section.top && scrollPosition < section.bottom) {
          activeId = section.id;
          break;
        }
      }

      setActiveSection(activeId);
    };

    // Initial check
    updateActiveSection();

    // Add scroll listener with throttling
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [isOnProductPage]);

  // Handle direct click on hash links
  const handleHashLink = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();

    // Extract the hash from the href
    const hash = href.split('#')[1];

    if (isOnProductPage) {
      // Already on the product page — scroll to the section
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to the product page with the hash. This is assumption (2) in
      // the docblock: it used to hardcode '/#' + hash, which would have sent
      // every off-page click to the OLD homepage no matter what the href said.
      navigate(`${PRODUCT_PATH}#${hash}`);
    }
  };

  const isActive = (sectionId: string) => {
    if (!isOnProductPage) {
      // Not on the product page, no active state
      return false;
    }

    // On the product page — check hash first (for direct navigation), then
    // scroll detection
    if (location.hash) {
      const hashSection = location.hash.replace('#', '');
      return hashSection === sectionId;
    }

    // No hash, use scroll-based detection
    return activeSection === sectionId;
  };

  // Anchors mirror /law-school/unlimited's grouped IA (FeatureGroup ids):
  //   practice = Study/Issue Spotter/Essay Coach · library = Outlines/Courses
  //   caselaw, notes, chat are their own sections.
  //
  // Both historically-dead anchors land here as their repaired targets:
  //   #outlines -> #library     #study -> #practice
  const featureItems = [
    { label: 'Study', href: '/law-school/unlimited#practice', icon: <Layers size={16} /> },
    { label: 'Issue Spotter', href: '/law-school/unlimited#practice', icon: <Target size={16} /> },
    { label: 'Essay Coach', href: '/law-school/unlimited#practice', icon: <PenLine size={16} /> },
    { label: 'Case Briefs', href: '/law-school/unlimited#caselaw', icon: <Glasses size={16} /> },
    { label: 'Outlines', href: '/law-school/unlimited#library', icon: <SquareGantt size={16} /> },
    { label: 'Courses', href: '/law-school/unlimited#library', icon: <MonitorPlay size={16} /> },
    { label: 'Notes', href: '/law-school/unlimited#notes', icon: <FileText size={16} /> },
    { label: 'Ask JDS', href: '/law-school/unlimited#chat', icon: <MessageSquare size={16} /> },
    // Community deliberately NOT in the marketing nav. Founder 2026-09-11:
    // "Community can be in the sidebar nav within the app... that's taking them
    // to the actual community. I don't need a link on the homepage."
    // It lives in the in-app sidebar (navigation-config.ts, COMMUNITY group),
    // which is the surface that reaches the real thing.
    // Do not re-add it here when community-board goes prod-ON; the app sidebar
    // is the answer.
  ];

  const navItems = [
    {
      label: 'Features',
      href: '#',
      isActive: featureItems.some(item => {
        const sectionId = item.href.split('#')[1];
        return isActive(sectionId);
      }),
      customElement: (() => {
        const isFeaturesActive = featureItems.some(item => {
          const sectionId = item.href.split('#')[1];
          return isActive(sectionId);
        });
        return (
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button className={`relative px-3 py-2 transition-colors flex items-center gap-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-[#F37022] after:transition-all ${
                isFeaturesActive
                  ? 'text-[#F37022] after:w-full'
                  : 'text-gray-700 dark:text-gray-300 hover:text-[#F37022] after:w-0 hover:after:w-full'
              }`}>
              Features
              <ChevronDown size={16} />
            </button>
          </DropdownMenuTrigger>
          {/* w-56, single-line. NOT widened to the w-80 two-line treatment the
              root nav's product menus use: these eight rows are SECTIONS of one
              page, not products, so there is no price or availability line to
              show and the extra width would be empty. */}
          <DropdownMenuContent align="start" className="w-56 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
            {featureItems.map((item) => (
              <DropdownMenuItem key={item.label} asChild className="hover:bg-[#F37022]/10 focus:bg-[#F37022]/10 hover:text-[#F37022] focus:text-[#F37022]">
                <a
                  href={item.href}
                  className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300"
                  onClick={(e) => {
                    e.preventDefault();
                    setDropdownOpen(false); // Close the dropdown
                    const hash = item.href.split('#')[1];
                    if (isOnProductPage) {
                      const element = document.getElementById(hash);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    } else {
                      navigate(item.href);
                    }
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        );
      })(),
      // Mobile-specific rendering: provide featureItems as children
      mobileChildren: featureItems
    },
    {
      label: 'Outlines',
      href: '/features/outlines',
      isActive: stripSlash(location.pathname) === '/features/outlines',
    },
    {
      label: 'Pricing',
      href: '/law-school/unlimited#pricing',
      isActive: isActive('pricing'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleHashLink(e, '/law-school/unlimited#pricing')
    },
    {
      label: 'NextGen Practice',
      href: '/bar/practice',
      isActive: false,
      icon: <Scale size={14} />,
    },
    {
      label: 'Blog',
      href: 'https://jdsimplified.com/blog/',
      isActive: false,
    },
  ];

  return (
    <Navbar
      siteName="askjds"
      navItems={navItems}
      logoSize="md"
      contextLabel="Law School"
      contextColor="jdblue"
      dashboardText="Access"
      dashboardHref="/chat"
    />
  );
}
